<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import LabelsAPI from 'dashboard/api/labels';
import AddLabel from 'dashboard/components-next/label/AddLabel.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  known: { type: Boolean, default: false },
  editable: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const { accountId } = useAccount();
const { run, abort, isPending } = useAbortableRequest();
const available = ref([]);
const hasError = ref(false);
const load = async () => {
  abort();
  available.value = [];
  hasError.value = false;
  if (!props.editable || !props.known) return;
  try {
    await run(async signal => {
      const { data } = await LabelsAPI.get();
      if (signal.aborted) return;
      if (!Array.isArray(data.payload))
        throw new Error('Invalid labels response');
      available.value = data.payload;
    });
  } catch {
    hasError.value = true;
  }
};
watch([accountId, () => props.editable, () => props.known], load, {
  immediate: true,
});
const blocked = computed(
  () => !props.known || props.disabled || isPending.value || hasError.value
);
const menuItems = computed(() =>
  available.value.map(label => ({
    label: label.title,
    value: label.id,
    thumbnail: { name: label.title, color: label.color },
    isSelected: props.modelValue.includes(label.title),
    action: 'dealLabel',
  }))
);
const change = titles => {
  if (!props.editable || blocked.value) return;
  emit('update:modelValue', [...new Set(titles)].sort());
};
const toggle = ({ value }) => {
  const label = available.value.find(item => item.id === value);
  if (!label) return;
  change(
    props.modelValue.includes(label.title)
      ? props.modelValue.filter(title => title !== label.title)
      : [...props.modelValue, label.title]
  );
};
</script>

<template>
  <section
    class="flex flex-col gap-2"
    :aria-label="t('KANBAN.DEAL_LABELS.TITLE')"
  >
    <fieldset :disabled="blocked" class="min-w-0 m-0 p-0 border-0">
      <legend class="mb-2 text-xs font-medium text-n-slate-11">
        {{ t('KANBAN.DEAL_LABELS.TITLE') }}
      </legend>
      <div class="flex flex-wrap items-center gap-2">
        <span
          v-for="title in modelValue"
          :key="title"
          class="inline-flex max-w-full items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-12"
        >
          <span
            v-if="available.some(label => label.title === title)"
            class="size-2 shrink-0 rounded-sm"
            :style="{
              backgroundColor: available.find(label => label.title === title)
                ?.color,
            }"
            aria-hidden="true"
          />
          <span class="break-words min-w-0">{{ title }}</span>
          <button
            v-if="editable"
            type="button"
            class="flex size-6 shrink-0 items-center justify-center rounded hover:bg-n-alpha-3 focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-40"
            :aria-label="t('KANBAN.DEAL_LABELS.REMOVE', { label: title })"
            @click="change(modelValue.filter(item => item !== title))"
          >
            <span class="i-lucide-x size-3.5" aria-hidden="true" />
          </button>
        </span>
        <Spinner v-if="isPending" />
        <AddLabel
          v-else-if="editable && known && !hasError"
          :label-menu-items="menuItems"
          @update-label="toggle"
        />
        <Button
          v-if="editable && known && !hasError"
          variant="ghost"
          size="sm"
          icon="i-lucide-refresh-cw"
          :disabled="blocked"
          :aria-label="t('KANBAN.DEAL_LABELS.REFRESH')"
          @click="load"
        />
      </div>
    </fieldset>
    <p v-if="!known" role="status" class="m-0 text-xs text-n-slate-11">
      {{ t('KANBAN.DEAL_LABELS.UNKNOWN') }}
    </p>
    <p v-if="hasError" role="alert" class="m-0 text-xs text-n-ruby-11">
      {{ t('KANBAN.DEAL_LABELS.ERROR') }}
    </p>
    <Button
      v-if="hasError"
      :disabled="disabled"
      size="sm"
      class="self-start"
      :label="t('KANBAN.TASKS.REFRESH')"
      @click="load"
    />
    <p
      v-else-if="known && !modelValue.length && !isPending"
      class="m-0 text-xs text-n-slate-10"
    >
      {{ t('KANBAN.DEAL_LABELS.EMPTY') }}
    </p>
    <p v-if="editable && known" class="m-0 text-xs text-n-slate-10">
      {{ t('KANBAN.DEAL_LABELS.SAVE_HINT') }}
    </p>
  </section>
</template>
